import React from "react";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import { ChapterMenu } from "../../ChapterMenu";
import styles from "../Wrapper/style.module.scss";

/**
 * Menu:
 *  - Single Menu for Notebook
 */
export const Menu = React.forwardRef(function Menu(props, ref) {
  const intl = useIntl();
  const onClick = () => !props.current && props.onSelect();

  let isVisted = props.visited;
  let isCompleted = props.visited;

  if (props.maxProgress > 0) {
    isCompleted = props.currentProgress === props.maxProgress;
    isVisted = props.visited && isCompleted;
  }

  // Track state transitions and announce only when they happen
  const [announcement, setAnnouncement] = React.useState("");
  const previousStateRef = React.useRef({
    completed: isCompleted,
    locked: props.locked,
  });

  React.useEffect(() => {
    // Check for completion transition (incomplete -> complete)
    if (!previousStateRef.current.completed && isCompleted) {
      const message = intl.formatMessage(
        {
          id: "Section {number} completed",
          defaultMessage: "Section {number} completed",
          description: "Announcement when section completes",
        },
        { number: props.number }
      );
      setAnnouncement(message);
    }
    // Check for unlock transition (locked -> unlocked)
    else if (previousStateRef.current.locked && !props.locked) {
      const message = intl.formatMessage(
        {
          id: "Section {number} unlocked",
          defaultMessage: "Section {number} unlocked",
          description: "Announcement when section unlocks",
        },
        { number: props.number }
      );
      setAnnouncement(message);
    }

    previousStateRef.current = { completed: isCompleted, locked: props.locked };
  }, [isCompleted, props.locked, props.number, intl]);

  const getClassName = (sub = "") => props.className + sub;

  const getAdjustedNumber = () => {
    const menu = props.menuList;
    const propsNumber = props.number;

    if (!menu || !Array.isArray(menu)) {
      return propsNumber;
    }

    const currentIndex = propsNumber - 1;
    const hiddenBefore = menu
      .slice(0, currentIndex)
      .filter((item) => item.visible === false).length;

    return propsNumber - hiddenBefore;
  };

  const getAriaLabel = () => {
    const adjustedNumber = getAdjustedNumber();
    const titleText = props.title ? props.title.replace(/<[^>]*>/g, "") : "";

    const parts = [];

    // Section number
    parts.push(
      intl.formatMessage(
        {
          id: "Section {number}",
          defaultMessage: "Section {number}",
          description: "Section number for navigation",
        },
        { number: adjustedNumber }
      )
    );

    // Lock status (only show if locked)
    if (props.locked) {
      parts.push(
        intl.formatMessage({
          id: "locked",
          defaultMessage: "locked",
          description: "Locked status for navigation",
        })
      );
    }

    // Title
    if (titleText) {
      parts.push(titleText);
    }

    // Progress (this implicitly shows completion when current === max)
    if (props.maxProgress > 0 && props.displayProgress) {
      parts.push(
        intl.formatMessage(
          {
            id: "{current} of {max}",
            defaultMessage: "{current} of {max}",
            description: "Progress indicator for navigation",
          },
          {
            current: props.currentProgress,
            max: props.maxProgress,
          }
        )
      );
    } else if (isCompleted) {
      // Only say "completed" if there's no progress numbers to show
      parts.push(
        intl.formatMessage({
          id: "completed",
          defaultMessage: "completed",
          description: "Completed status for navigation",
        })
      );
    }

    return parts.join(", ");
  };

  return (
    <ChapterMenu.Wrapper
      className={getClassName(isVisted ? " visited_chapter" : "")}
      onClick={onClick}
      onEnterKey={onClick}
      tabIndex={props.tabIndex}
      ref={ref}
      ariaLabel={getAriaLabel()}
    >
      {/* Screen reader announcement - only has content during state transitions */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className={styles.srOnly}
      >
        {announcement}
      </div>

      <ChapterMenu.IndexNumber
        className={getClassName("_indexNumber")}
        number={props.number}
        visited={isVisted}
        completed={isCompleted}
        locked={props.locked}
        menuList={props.menuList}
      />
      <ChapterMenu.Label
        className={getClassName("_label")}
        title={props.title}
        maxProgress={props.maxProgress}
        displayProgress={props.displayProgress}
        currentProgress={props.currentProgress}
      />
    </ChapterMenu.Wrapper>
  );
});

Menu.propTypes = {
  /**
   * Callback fired when a Menu is Selected
   */
  onSelect: PropTypes.func,
  /**
   * Title to display in  menu
   */
  title: PropTypes.string.isRequired,
  /**
   * Boolean specifying if menu is selected
   */
  current: PropTypes.bool.isRequired,
  /**
   * Index number of menu
   */
  number: PropTypes.number.isRequired,
  /**
   * TabIndex to set on Menu
   */
  tabIndex: PropTypes.number,
  /**
   * ClassName
   */
  className: PropTypes.string,
  /**
   * Boolean set if Section is visited
   */
  visited: PropTypes.bool,
};

Menu.defaultProps = {
  className: "",
  title: "",
  number: 0,
  tabIndex: 0,
};
